/**
 * SCH-4.5: Sync History tab. Paginated list of past sync runs for this
 * tenant. Click "View" on any row to re-open the BackfillPreviewModal in
 * read-only progress mode (reuses SCH-3.5's readOnly pattern).
 */

import { useMemo, useState } from 'react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { Eye, Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useSyncHistory } from '@/hooks/integrations/useBackfillProgress';
import type {
  SyncHistoryItem,
  SyncHistoryStatus,
  SyncHistoryType,
} from '@/types/integrations/backfillProgress';

import { BackfillPreviewModal } from './BackfillPreviewModal';

const PAGE_SIZE = 25;
const STATUS_FILTER_OPTIONS: Array<{ value: 'all' | SyncHistoryStatus; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'completed', label: 'Completed' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'paused', label: 'Paused' },
  { value: 'aborted', label: 'Aborted' },
];
const TYPE_FILTER_OPTIONS: Array<{ value: 'all' | SyncHistoryType; label: string }> = [
  { value: 'all', label: 'All types' },
  { value: 'manual', label: 'Manual' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'poc', label: 'POC (single project)' },
];

function _statusBadge(status: SyncHistoryStatus) {
  const tone =
    status === 'completed'
      ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
      : status === 'aborted'
        ? 'border-amber-300 bg-amber-50 text-amber-800'
        : status === 'paused'
          ? 'border-yellow-300 bg-yellow-50 text-yellow-800'
          : 'border-blue-300 bg-blue-50 text-blue-800';
  // SCH-BUG-9b (P-E4-6): guard against an empty/unexpected status — `status[0]`
  // on '' threw and blanked the whole table.
  const label =
    status === 'in_progress'
      ? 'In progress'
      : status
        ? status[0].toUpperCase() + status.slice(1)
        : 'Unknown';
  return (
    <Badge variant="outline" className={tone}>
      {label}
    </Badge>
  );
}

function _typeBadge(type: SyncHistoryType) {
  const label =
    type === 'poc' ? 'POC' : type ? type[0].toUpperCase() + type.slice(1) : 'Unknown';
  return (
    <Badge variant="outline" className="border-border bg-card text-foreground">
      {label}
    </Badge>
  );
}

function _formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 1) return '<1s';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function _formatAbsolute(iso: string): string {
  try {
    return parseISO(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function _formatRelative(iso: string): string {
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

export function SyncHistoryTab() {
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<'all' | SyncHistoryStatus>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | SyncHistoryType>('all');
  // SCH-3.5 readOnly modal wiring — when set, opens BackfillPreviewModal
  // in progress + readOnly mode for that run.
  const [viewRunId, setViewRunId] = useState<string | null>(null);

  const { data: envelope, isLoading, error, isFetching } = useSyncHistory({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    status: statusFilter === 'all' ? undefined : statusFilter,
    type: typeFilter === 'all' ? undefined : typeFilter,
  });

  const rows: SyncHistoryItem[] = envelope?.data?.data ?? [];
  const totalCount = envelope?.data?.totalCount ?? 0;
  // When filters are active, totalCount is approximate (server returns
  // unfiltered total). Pagination uses unfiltered totals; filtered pages
  // may be shorter than PAGE_SIZE — acceptable per AC notes.
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const hasFilters = statusFilter !== 'all' || typeFilter !== 'all';

  const requestError = envelope?.error || error;

  function handleResetPage() {
    setPage(0);
  }

  return (
    <div className="space-y-4">
      {/* Header + filter row */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-sm font-medium">Past sync runs</h3>
          <p className="text-xs text-muted-foreground">
            {totalCount} {totalCount === 1 ? 'run' : 'runs'} total
            {hasFilters ? ' (filtered)' : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v as 'all' | SyncHistoryStatus);
              handleResetPage();
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTER_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={typeFilter}
            onValueChange={(v) => {
              setTypeFilter(v as 'all' | SyncHistoryType);
              handleResetPage();
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPE_FILTER_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Loading / error / empty / data states */}
      {isLoading && (
        <div className="flex items-center gap-2 rounded-md border bg-card p-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading sync history…
        </div>
      )}

      {!isLoading && requestError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {('message' in (requestError as object) &&
            (requestError as { message: string }).message) ||
            'Failed to load sync history.'}
        </div>
      )}

      {!isLoading && !requestError && rows.length === 0 && (
        <div className="rounded-md border bg-card p-6 text-center text-sm text-muted-foreground">
          {hasFilters
            ? 'No runs match the current filters.'
            : 'No sync runs yet. Start a backfill from the Configure → Connection tab.'}
        </div>
      )}

      {!isLoading && rows.length > 0 && (
        <div className="rounded-md border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2 font-medium">When</th>
                <th className="text-left px-3 py-2 font-medium">Type</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-right px-3 py-2 font-medium">Projects</th>
                <th className="text-right px-3 py-2 font-medium">Duration</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((row) => (
                <HistoryRow
                  key={row.syncRunId}
                  row={row}
                  onView={() => setViewRunId(row.syncRunId)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalCount > PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div>
            Page {page + 1} of {totalPages}
            {isFetching && (
              <Loader2 className="ml-2 inline h-3 w-3 animate-spin align-middle" />
            )}
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0 || isFetching}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1 || isFetching}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Read-only run viewer — reuses SCH-3.5's BackfillPreviewModal readOnly mode. */}
      {viewRunId && (
        <BackfillPreviewModal
          isOpen={true}
          onClose={() => setViewRunId(null)}
          initialSyncRunId={viewRunId}
          readOnly={true}
        />
      )}
    </div>
  );
}

function HistoryRow({
  row,
  onView,
}: {
  row: SyncHistoryItem;
  onView: () => void;
}) {
  const projectsCell = useMemo(() => {
    const parts: string[] = [];
    if (row.projectsSucceeded > 0) parts.push(`✓ ${row.projectsSucceeded}`);
    if (row.projectsSkipped > 0) parts.push(`skipped ${row.projectsSkipped}`);
    if (row.projectsFailed > 0) parts.push(`✗ ${row.projectsFailed}`);
    return parts.length > 0 ? parts.join(' · ') : '—';
  }, [row]);

  return (
    <tr className="hover:bg-muted/20">
      <td className="px-3 py-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help">{_formatRelative(row.startedAt)}</span>
            </TooltipTrigger>
            <TooltipContent>{_formatAbsolute(row.startedAt)}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </td>
      <td className="px-3 py-2">{_typeBadge(row.type)}</td>
      <td className="px-3 py-2">{_statusBadge(row.status)}</td>
      <td className="px-3 py-2 text-right">{projectsCell}</td>
      <td className="px-3 py-2 text-right text-muted-foreground">
        {_formatDuration(row.durationSeconds)}
      </td>
      <td className="px-3 py-2 text-right">
        <Button size="sm" variant="ghost" onClick={onView} className="gap-1.5">
          <Eye className="h-3.5 w-3.5" />
          View
        </Button>
      </td>
    </tr>
  );
}
